import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, Lock, AlertTriangle } from 'lucide-react';

interface PinEntryDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (pin: string) => Promise<{ success: boolean; message: string; pinAttemptsRemaining?: number; lockedUntil?: string }>;
  title?: string;
}

export default function PinEntryDialog({ open, onClose, onSubmit, title = 'Enter Assessor PIN' }: PinEntryDialogProps) {
  const [digits, setDigits] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [locked, setLocked] = useState(false);
  const [lockCountdown, setLockCountdown] = useState('');
  const inputRefs = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)];

  useEffect(() => {
    if (open) {
      setDigits(['', '', '', '']);
      setError('');
      setLocked(false);
      setTimeout(() => inputRefs[0].current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    if (!locked) return;
    const timer = setInterval(() => {
      setLockCountdown(prev => {
        // Just keep the error message visible
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [locked]);

  const handleDigit = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...digits];
    next[index] = value;
    setDigits(next);
    setError('');
    if (value && index < 3) inputRefs[index + 1].current?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handleSubmit = async () => {
    const pin = digits.join('');
    if (pin.length !== 4) { setError('Enter all 4 digits'); return; }
    setLoading(true);
    setError('');
    const result = await onSubmit(pin);
    setLoading(false);
    if (!result.success) {
      setError(result.message);
      setDigits(['', '', '', '']);
      if (result.lockedUntil) setLocked(true);
      setTimeout(() => inputRefs[0].current?.focus(), 100);
    }
    // If success, parent handles closing
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[60] flex items-center justify-center px-4">
      <div className="w-full max-w-xs bg-card border border-border rounded-2xl p-6 text-center shadow-lg">
        <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Lock className="w-7 h-7 text-primary" />
        </div>
        <h2 className="text-lg font-bold text-foreground mb-1">{title}</h2>
        <p className="text-xs text-muted-foreground mb-5">Enter your 4-digit assessor PIN to confirm</p>

        <div className="flex gap-3 justify-center mb-4">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={inputRefs[i]}
              type="password"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={e => handleDigit(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              disabled={locked || loading}
              className="w-12 h-14 text-center text-2xl font-bold border-2 border-border rounded-xl bg-background text-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all disabled:opacity-50"
            />
          ))}
        </div>

        {error && (
          <div className="flex items-center gap-1.5 text-destructive text-xs mb-4 justify-center">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="space-y-2">
          <Button onClick={handleSubmit} disabled={loading || locked || digits.some(d => !d)} className="w-full h-11">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Verifying…</> : 'Confirm PIN'}
          </Button>
          <Button variant="ghost" onClick={onClose} className="w-full text-xs" disabled={loading}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}
